import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useMemo, useEffect } from 'react';
import { addDays, isSameDay } from 'date-fns';
import { MenuItem, InlineSelect, Tooltip, Typography, useStateInRef, WithInfoIcon, InlineSelectDatePicker, KeyboardDatePicker } from 'seagull';
import { useIoContext } from '~/contexts';
import { useOnMount } from '~/hooks/useOnMount';
import { useLocaleKeys } from '~/i18n';
import { serviceModel } from '~/models';
import { DiscountExpirationType, LimitedTimeDiscountOfferModel } from '~/models/LimitedTimeDiscountOfferModel';
import { validateLimitedTimeDiscountOffer } from '~/models/service';
import { fromDateStampObject, toDateStampObject } from '~/models/time';
import pluralize from '~/utils/pluralize';
import { AdvancedSettingsControl } from '../AdvancedSettingsControl/AdvancedSettingsControl';
import styles from '../ServiceAdvancedSettings.module.scss';
import { AdvancedSetting } from '../ServiceAdvancedSettings.types';
import { TextAndInlineSelectRow } from '../TextAndInlineSelectRow';
import { getDaysOptions, getDateRange } from './LimitedTimeDiscountOfferControl.utils';
export function LimitedTimeDiscountOfferControl(props) {
    const { service, info, label, isReadOnly, onLimitedTimeDiscountOfferChange, disabled, testId: testPrefix, relationshipState, disabledTooltip, featureFlags } = props;
    const { getNow } = useIoContext();
    const { t: advTexts } = useLocaleKeys('components.serviceAdvancedSettings');
    const getProps = useStateInRef(props);
    const now = useMemo(()=>getNow(), []);
    const [timeframeType, setTimeframeType] = useState(getTimeframeType());
    const [daysCount, setDaysCount] = useState(30);
    const [selectedDate, setSelectedDate] = useState(()=>addDays(now, 30));
    const [error, setError] = useState('');
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const { SERVICE_SETTINGS_SELECTORS = false } = featureFlags;
    if (!onLimitedTimeDiscountOfferChange || relationshipState.type === 'approved') {
        return null;
    }
    const limitedTimeDiscountOffer = serviceModel.limitedTimeDiscountOffer(service);
    const discount = useMemo(()=>service.cost.discount, [
        service.cost.discount
    ]);
    function getTimeframeType() {
        const expirationType = serviceModel.limitedTimeDiscountOfferExpirationType(service);
        return expirationType === DiscountExpirationType.Date ? 'until' : 'within';
    }
    useOnMount(()=>{
        const type = getTimeframeType();
        setTimeframeType(type);
        if (type === 'within') {
            const days = limitedTimeDiscountOffer === null || limitedTimeDiscountOffer === void 0 ? void 0 : limitedTimeDiscountOffer.ExpirationDays();
            setDaysCount(days || 30);
        } else {
            const date = limitedTimeDiscountOffer === null || limitedTimeDiscountOffer === void 0 ? void 0 : limitedTimeDiscountOffer.ExpirationDate();
            setSelectedDate(date ? fromDateStampObject(date) : addDays(now, 30));
        }
    });
    useEffect(()=>{
        const error = validateLimitedTimeDiscountOffer(service, now);
        setError(error);
        const currentOffer = serviceModel.limitedTimeDiscountOffer(service);
        const expirationType = serviceModel.limitedTimeDiscountOfferExpirationType(service);
        if (currentOffer && expirationType) {
            if (expirationType === DiscountExpirationType.Days) {
                const days = currentOffer.ExpirationDays();
                if (days !== undefined) {
                    setTimeframeType('within');
                    setDaysCount(days);
                }
            } else {
                const date = currentOffer.ExpirationDate();
                if (date) {
                    setTimeframeType('until');
                    setSelectedDate(fromDateStampObject(date));
                }
            }
        }
        if (!service.cost.discount) {
            setTimeframeType('until');
            setDaysCount(30);
            setSelectedDate(addDays(getNow(), 30));
            if (currentOffer) {
                var _getProps_onLimitedTimeDiscountOfferChange, _getProps;
                (_getProps_onLimitedTimeDiscountOfferChange = (_getProps = getProps()).onLimitedTimeDiscountOfferChange) === null || _getProps_onLimitedTimeDiscountOfferChange === void 0 ? void 0 : _getProps_onLimitedTimeDiscountOfferChange.call(_getProps, service.id, undefined);
            }
        }
    }, [
        service,
        now,
        getNow
    ]);
    const dayOptions = useMemo(()=>{
        return getDaysOptions();
    }, []);
    const { minDate, maxDate } = useMemo(()=>{
        return getDateRange(now);
    }, [
        now
    ]);
    const getExpirationByTimeframe = (timeframe)=>{
        if (timeframe === 'within') {
            return {
                type: DiscountExpirationType.Days,
                days: daysCount
            };
        } else {
            return {
                type: DiscountExpirationType.Date,
                date: toDateStampObject(selectedDate)
            };
        }
    };
    const handleChange = (enabled)=>{
        if (!onLimitedTimeDiscountOfferChange) return;
        if (enabled) {
            const existingDiscount = service.cost.discount;
            if (!existingDiscount) {
                return;
            }
            const expiration = getExpirationByTimeframe(timeframeType);
            const offer = new LimitedTimeDiscountOfferModel(expiration);
            onLimitedTimeDiscountOfferChange(service.id, offer);
        } else {
            onLimitedTimeDiscountOfferChange(service.id, undefined);
        }
    };
    const handleTimeframeTypeChange = (newType)=>{
        setTimeframeType(newType);
        const expiration = getExpirationByTimeframe(newType);
        if (limitedTimeDiscountOffer && discount) {
            const updatedOffer = new LimitedTimeDiscountOfferModel(expiration);
            onLimitedTimeDiscountOfferChange === null || onLimitedTimeDiscountOfferChange === void 0 ? void 0 : onLimitedTimeDiscountOfferChange(service.id, updatedOffer);
        }
    };
    const handleDaysChange = (newDays)=>{
        setDaysCount(newDays);
        if (timeframeType === 'within') {
            const currentOffer = limitedTimeDiscountOffer;
            if (currentOffer) {
                const updatedOffer = currentOffer.setExpirationDays(newDays);
                onLimitedTimeDiscountOfferChange === null || onLimitedTimeDiscountOfferChange === void 0 ? void 0 : onLimitedTimeDiscountOfferChange(service.id, updatedOffer);
            }
        }
    };
    const handleDateChange = (newDate)=>{
        setSelectedDate(newDate);
        if (timeframeType === 'until') {
            const currentOffer = limitedTimeDiscountOffer;
            if (currentOffer) {
                const updatedOffer = currentOffer.setExpirationDate(toDateStampObject(newDate));
                onLimitedTimeDiscountOfferChange === null || onLimitedTimeDiscountOfferChange === void 0 ? void 0 : onLimitedTimeDiscountOfferChange(service.id, updatedOffer);
            }
        }
    };
    const shouldShowDefaultView = disabled || !limitedTimeDiscountOffer;
    const adjustedMinDate = useMemo(()=>{
        return selectedDate < minDate ? selectedDate : minDate;
    }, [
        selectedDate,
        minDate
    ]);
    return /*#__PURE__*/ _jsx(AdvancedSettingsControl, {
        info: shouldShowDefaultView ? info : '',
        infoTestId: "".concat(testPrefix, "-limited-time-discount-info"),
        disabled: disabled,
        label: shouldShowDefaultView ? label : '',
        isReadOnly: isReadOnly,
        checked: !disabled && !!limitedTimeDiscountOffer,
        testId: "".concat(testPrefix, "-limited-time-discount"),
        disabledTooltip: shouldShowDefaultView ? disabledTooltip : '',
        onChange: handleChange,
        labelSuffix: !shouldShowDefaultView && /*#__PURE__*/ _jsx(WithInfoIcon, {
            className: styles.rowAndIconContainer,
            info: info,
            infoTestId: "".concat(testPrefix, "-limited-time-discount-info"),
            children: /*#__PURE__*/ _jsxs(TextAndInlineSelectRow, {
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        children: advTexts.applyDiscountOnlyIf()
                    }),
                    /*#__PURE__*/ _jsxs(InlineSelect, {
                        skin: "primary",
                        disabled: disabled,
                        value: timeframeType,
                        testId: "".concat(testPrefix, "-timeframe-type"),
                        onChange: (e)=>handleTimeframeTypeChange(e.target.value),
                        children: [
                            /*#__PURE__*/ _jsx(MenuItem, {
                                value: "within",
                                children: "within"
                            }),
                            /*#__PURE__*/ _jsx(MenuItem, {
                                value: "until",
                                children: "until"
                            })
                        ]
                    }),
                    timeframeType === 'within' ? /*#__PURE__*/ _jsx(InlineSelect, {
                        skin: "primary",
                        disabled: disabled,
                        value: daysCount,
                        testId: "".concat(testPrefix, "-days-count"),
                        isSelected: (itemValue, currentValue)=>Number(itemValue) === Number(currentValue),
                        onChange: (e)=>handleDaysChange(Number(e.target.value)),
                        children: dayOptions.map((day)=>/*#__PURE__*/ _jsx(MenuItem, {
                                value: day.toString(),
                                testId: "".concat(testPrefix, "-days-count-option-").concat(day),
                                children: pluralize('day', day, true)
                            }, day))
                    }) : /*#__PURE__*/ _jsx(Tooltip, {
                        title: isCalendarOpen ? '' : error,
                        placement: "top",
                        children: /*#__PURE__*/ _jsx("div", {
                            style: {
                                position: 'relative'
                            },
                            onFocus: ()=>setIsCalendarOpen(true),
                            onBlur: ()=>setIsCalendarOpen(false),
                            children: SERVICE_SETTINGS_SELECTORS ? /*#__PURE__*/ _jsx(InlineSelectDatePicker, {
                                size: "small",
                                testId: "".concat(testPrefix, "-deadline-date"),
                                className: styles.noShrink,
                                value: selectedDate,
                                minDate: adjustedMinDate,
                                maxDate: maxDate,
                                disabled: disabled,
                                disableErrors: !error,
                                error: !!error,
                                skin: "primary",
                                format: "MMM d, yyyy",
                                onChange: (newDate)=>newDate && handleDateChange(newDate),
                                shouldDisableDate: (date)=>!!date && (date < minDate || date > maxDate) && !isSameDay(date, selectedDate)
                            }) : /*#__PURE__*/ _jsx(KeyboardDatePicker, {
                                style: {
                                    width: 160,
                                    textAlign: 'center'
                                },
                                size: "small",
                                testId: "".concat(testPrefix, "-deadline-date"),
                                className: styles.noShrink,
                                value: selectedDate,
                                minDate: adjustedMinDate,
                                maxDate: maxDate,
                                disabled: disabled,
                                disableErrors: !error,
                                helperText: '',
                                error: !!error,
                                onChange: (newDate)=>newDate && handleDateChange(newDate),
                                shouldDisableDate: (date)=>!!date && (date < minDate || date > maxDate) && !isSameDay(date, selectedDate)
                            })
                        })
                    })
                ]
            })
        })
    }, AdvancedSetting.LimitedTimeDiscountOffer);
}
